import {
  CognitoUserPool,
  CognitoUserSession,
} from "amazon-cognito-identity-js";
import { User } from "../../../../models/types";

// ---------- Config ----------
const LAMBDA_BASE =
  "https://m01op8z79h.execute-api.us-east-2.amazonaws.com/default/avro-users";

// Use the same pool data you already use elsewhere in the app
const poolData = {
  UserPoolId: "us-east-2_nECr2VdPc",
  ClientId: "6jd4v12t5keoj2eupcrjkcia2c",
};
const userPool = new CognitoUserPool(poolData);

// ---------- Utilities ----------
class ApiError extends Error {
  status?: number;
  constructor(message: string, status?: number) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

async function fetchJson<T>(
  input: RequestInfo | URL,
  init: RequestInit & { timeoutMs?: number } = {}
): Promise<T> {
  const { timeoutMs = 15000, ...rest } = init;
  const ac = new AbortController();
  const timer = setTimeout(() => ac.abort(), timeoutMs);

  try {
    const res = await fetch(input, { ...rest, signal: ac.signal });

    if (!res.ok) {
      let detail = res.statusText;
      try {
        const j = await res.json();
        detail =
          (j && (j.error || j.message || JSON.stringify(j))) || detail || "";
      } catch {
        try {
          detail = await res.text();
        } catch {}
      }
      throw new ApiError(`${res.status} ${detail}`, res.status);
    }
    return (await res.json()) as T;
  } finally {
    clearTimeout(timer);
  }
}

// Returns the current Cognito ID token or throws.
export async function getIdToken(): Promise<string> {
  const currentUser = userPool.getCurrentUser();
  if (!currentUser) throw new Error("No current user");

  return new Promise<string>((resolve, reject) => {
    currentUser.getSession((err: any, session: CognitoUserSession | null) => {
      if (err)
        return reject(
          new Error(`Auth session error: ${err?.message || err}`)
        );
      if (!session || !session.isValid())
        return reject(new Error("Invalid/expired session"));
      resolve(session.getIdToken().getJwtToken());
    });
  });
}

// ---------- Core API ----------
/**
 * PUT partial fields to /avro-users?userId=...
 * (Uses Lambda's diffed update; only changed fields will be written.)
 */
export async function updateUser(
  userId: string,
  partial: Partial<User>,
  opts?: { idToken?: string; timeoutMs?: number; useBearer?: boolean }
): Promise<User> {
  const token = opts?.idToken ?? (await getIdToken());

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    // Match your existing thunks which send raw token in Authorization.
    // If your API Gateway authorizer expects "Bearer <token>", set useBearer: true.
    Authorization: opts?.useBearer ? `Bearer ${token}` : token,
  };

  const url = `${LAMBDA_BASE}?userId=${encodeURIComponent(userId)}`;
  return await fetchJson<User>(url, {
    method: "PUT",
    headers,
    body: JSON.stringify(partial),
    timeoutMs: opts?.timeoutMs,
  });
}

// ---------- Optional helpers ----------
export async function getUser(
  userId: string,
  opts?: { idToken?: string; timeoutMs?: number; useBearer?: boolean }
): Promise<User> {
  const token = opts?.idToken ?? (await getIdToken());
  const headers: Record<string, string> = {
    Authorization: opts?.useBearer ? `Bearer ${token}` : token,
  };
  const url = `${LAMBDA_BASE}?userId=${encodeURIComponent(userId)}`;
  return await fetchJson<User>(url, {
    method: "GET",
    headers,
    timeoutMs: opts?.timeoutMs,
  });
}

// ---------- PartnerClients helpers ----------
export type PartnerClient = {
  clientUserId: string;
  name?: string;
  email?: string;
  gender?: string;
  goals?: any;
  isProUser?: boolean;
};

function extractClientSummary(u: User): PartnerClient {
  // Build a safe display name without mixing ?? and || directly
  const providedName = (u as any).name as string | undefined;
  const first = ((u as any).firstName as string | undefined) ?? "";
  const last = ((u as any).lastName as string | undefined) ?? "";
  const computed = `${first} ${last}`.trim();
  const name = providedName ?? (computed.length > 0 ? computed : undefined);

  return {
    clientUserId: (u as any).userId,
    name,
    email: (u as any).email,
    gender: (u as any).gender,
    goals: (u as any).goals,
    isProUser: (u as any).isProUser === true,
  };
}

function upsertPartnerClient(
  list: PartnerClient[] | undefined,
  summary: PartnerClient
): PartnerClient[] {
  const arr = Array.isArray(list) ? [...list] : [];
  const i = arr.findIndex((c) => c?.clientUserId === summary.clientUserId);
  if (i >= 0) {
    arr[i] = { ...arr[i], ...summary };
  } else {
    arr.push(summary);
  }
  return arr;
}

function removePartnerClient(
  list: PartnerClient[] | undefined,
  clientUserId: string
): PartnerClient[] {
  const arr = Array.isArray(list) ? list : [];
  return arr.filter((c) => c?.clientUserId !== clientUserId);
}

// ---------- Convenience wrappers (FINAL) ----------
/**
 * Give the client Pro access AND bind them to a trainer.
 * - Sets client.isProUser = true
 * - Sets client.currentTrainer = trainerUserId
 * - Upserts client summary into partner.partnerClients
 *
 * Returns { client, partner }
 */
export async function giveClientProAccess(
  client: User | string,
  trainerUserId: string,
  extras?: {
    clientExtras?: Partial<User>;
    useBearerAuth?: boolean;
    timeoutMs?: number;
  }
): Promise<{ client: User; partner: User }> {
  const idToken = await getIdToken();
  const useBearer = !!extras?.useBearerAuth;
  const timeoutMs = extras?.timeoutMs;

  const clientUser =
    typeof client === "string"
      ? await getUser(client as string, { idToken, useBearer, timeoutMs })
      : (client as User);

  const clientUserId = (clientUser as any).userId as string;
  if (!clientUserId) throw new Error("Client user is missing userId");

  // 1) update client
  const clientPatch: Partial<User> = {
    isProUser: true,
    ...(extras?.clientExtras || {}),
    ...(trainerUserId ? { currentTrainer: trainerUserId as any } : {}),
  };

  const updatedClient = await updateUser(clientUserId, clientPatch, {
    idToken,
    useBearer,
    timeoutMs,
  });

  // 2) update partner.partnerClients
  const partner = await getUser(trainerUserId, { idToken, useBearer, timeoutMs });
  const partnerClients = upsertPartnerClient(
    (partner as any).partnerClients as PartnerClient[] | undefined,
    extractClientSummary(updatedClient)
  );

  const updatedPartner = await updateUser(
    trainerUserId,
    { partnerClients } as Partial<User>,
    { idToken, useBearer, timeoutMs }
  );

  return { client: updatedClient, partner: updatedPartner };
}

/**
 * Remove Pro access AND unbind from trainer.
 * - Sets client.isProUser = false
 * - Clears client.currentTrainer (null)
 * - Removes client from partner.partnerClients
 *
 * Returns { client, partner }
 */
export async function removeClientProAccess(
  client: User | string,
  trainerUserId: string,
  extras?: {
    useBearerAuth?: boolean;
    timeoutMs?: number;
  }
): Promise<{ client: User; partner: User }> {
  const idToken = await getIdToken();
  const useBearer = !!extras?.useBearerAuth;
  const timeoutMs = extras?.timeoutMs;

  const clientUser =
    typeof client === "string"
      ? await getUser(client as string, { idToken, useBearer, timeoutMs })
      : (client as User);

  const clientUserId = (clientUser as any).userId as string;
  if (!clientUserId) throw new Error("Client user is missing userId");

  // 1) update client
  const updatedClient = await updateUser(
    clientUserId,
    { isProUser: false, currentTrainer: null as any },
    { idToken, useBearer, timeoutMs }
  );

  // 2) update partner.partnerClients
  const partner = await getUser(trainerUserId, { idToken, useBearer, timeoutMs });
  const pruned = removePartnerClient(
    (partner as any).partnerClients as PartnerClient[] | undefined,
    clientUserId
  );

  const updatedPartner = await updateUser(
    trainerUserId,
    { partnerClients: pruned } as Partial<User>,
    { idToken, useBearer, timeoutMs }
  );

  return { client: updatedClient, partner: updatedPartner };
}
